import {
  Bookmark,
  Check,
  ChevronLeftIcon,
  ChevronRightIcon,
  Heart,
  History,
  Loader2,
  type LucideIcon,
  Search,
  SprayCan,
  X,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
  BOTTLE_LISTS,
  type BottleListId,
  LIBRARY_FILTER_LABELS,
  LIBRARY_LABEL,
  LIBRARY_TAB_LABELS,
  type LibraryEntry,
  type LibraryFilter,
  type LibraryTabId,
} from '~/src/components/library/libraryModel'
import { cn } from '~/src/lib/utils'

export const libraryTabId = (idPrefix: string, tab: LibraryTabId) =>
  `${idPrefix}-tab-${tab}`
export const libraryPanelId = (idPrefix: string, tab: LibraryTabId) =>
  `${idPrefix}-panel-${tab}`

/** A square picture, with a fallback when it is missing or dead. */
export const Thumb = ({
  src,
  fragrance,
  className = 'size-10 rounded-lg',
}: {
  src?: string | null
  fragrance?: boolean
  className?: string
}) => {
  const [broken, setBroken] = useState(false)
  return (
    <div
      className={cn(
        'relative aspect-square shrink-0 overflow-hidden',
        className
      )}
    >
      {src && !broken ? (
        <Image
          src={src}
          onError={() => setBroken(true)}
          alt=''
          unoptimized
          width={40}
          height={40}
          className='h-full w-full object-cover'
        />
      ) : fragrance ? (
        <div className='flex h-full w-full items-center justify-center'>
          <SprayCan aria-hidden className='size-1/2 opacity-40' />
        </div>
      ) : null}
      <div className='absolute inset-0 bg-black/5' />
    </div>
  )
}

/** The fragrance pages' icons for each list: a heart, a tick, a bookmark. */
const LIST_ICON: Record<BottleListId, LucideIcon> = {
  loved: Heart,
  owned: Check,
  wanted: Bookmark,
}
/** Each list's icon when on, in the fragrance pages' colors. */
const LIST_ICON_ON: Record<BottleListId, string> = {
  loved: 'fill-current text-ds-fg-error-primary',
  owned: 'text-ink dark:text-white',
  wanted: 'fill-current text-ds-text-brand-accent',
}
const isBottleList = (filter: LibraryFilter): filter is BottleListId =>
  filter in LIST_ICON

const ListIcon = ({
  list,
  on,
  off = 'opacity-60',
  className,
}: {
  list: BottleListId
  on: boolean
  /** How it looks when off: a filter's is plainer than a row's. */
  off?: string
  className?: string
}) => {
  const Icon = LIST_ICON[list]
  return (
    <Icon
      aria-hidden
      className={cn('shrink-0', on ? LIST_ICON_ON[list] : off, className)}
    />
  )
}

const ROW_CLASSES =
  'group flex shrink-0 items-center gap-3 rounded-lg px-2 py-1.5 outline-none hover:bg-black/5 focus-visible:bg-black/5 dark:hover:bg-white/5'

/** One row, the same in every tab: a picture, a title, a line of details. */
export const LibraryRow = ({
  entry,
  onClick,
}: {
  entry: LibraryEntry
  onClick?: () => void
}) => (
  <Link
    href={entry.href}
    onClick={onClick}
    {...(entry.external ? { target: '_blank', rel: 'noopener' } : {})}
    data-library-row
    className={ROW_CLASSES}
  >
    <Thumb src={entry.image} fragrance={entry.fragrance} />
    <div className='min-w-0 flex-1'>
      <div className='line-clamp-1 text-sm/tight opacity-80 transition-opacity md:group-hover:opacity-100'>
        {entry.title}
      </div>
      {entry.subtitle && (
        <div className='mt-0.5 line-clamp-1 text-xs/tight opacity-50'>
          {entry.subtitle}
        </div>
      )}
    </div>
    {entry.lists && (
      <span className='flex shrink-0 items-center gap-1.5'>
        <span className='sr-only'>
          {entry.lists.map((l) => LIBRARY_FILTER_LABELS[l]).join(', ')}
        </span>
        {BOTTLE_LISTS.map((l) => (
          <ListIcon
            key={l}
            list={l}
            on={entry.lists?.includes(l) ?? false}
            off='opacity-15'
            className='size-3.5'
          />
        ))}
      </span>
    )}
    <ChevronRightIcon
      aria-hidden
      className='size-4 shrink-0 opacity-30 transition-opacity md:group-hover:opacity-60'
    />
  </Link>
)

interface TabsProps {
  tabs: LibraryTabId[]
  active: LibraryTabId
  counts: Record<LibraryTabId, string>
  /** A bottle of theirs, drawn beside "Bottles" so the tab stands out. */
  bottleImage?: string | null
  onPick: (tab: LibraryTabId) => void
  /** Keeps the dropdown's and the side menu's ids apart. */
  idPrefix: string
  className?: string
}

/** Each tab's icon; Bottles shows one of their bottles when it has one. */
const TAB_ICON: Record<LibraryTabId, LucideIcon> = {
  recents: History,
  saves: Bookmark,
  bottles: SprayCan,
}

/** Recents / Saves / Bottles: a segmented bar, an icon over each label, the picked one raised with its count. */
export const LibraryTabs = ({
  tabs,
  active,
  counts,
  bottleImage,
  onPick,
  idPrefix,
  className,
}: TabsProps) => (
  <div
    role='tablist'
    aria-label={LIBRARY_LABEL}
    className={cn(
      'flex shrink-0 gap-1 rounded-2xl bg-black/5 p-1 dark:bg-white/10',
      className
    )}
  >
    {tabs.map((tab) => {
      const on = tab === active
      const Icon = TAB_ICON[tab]
      return (
        <button
          key={tab}
          id={libraryTabId(idPrefix, tab)}
          type='button'
          role='tab'
          aria-selected={on}
          aria-controls={libraryPanelId(idPrefix, tab)}
          onClick={() => onPick(tab)}
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-0.5 whitespace-nowrap rounded-xl px-2 py-1.5 text-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black/20 dark:focus-visible:ring-white/30',
            on
              ? 'bg-white font-semibold text-ink shadow-sm dark:bg-white/15 dark:text-white'
              : 'font-medium text-foreground-secondary hover:text-ink dark:hover:text-white'
          )}
        >
          <span className='flex h-5 items-center'>
            {tab === 'bottles' && bottleImage ? (
              <Thumb
                src={bottleImage}
                fragrance
                className='size-5 rounded-full bg-white'
              />
            ) : (
              <Icon aria-hidden className='size-4' />
            )}
          </span>
          <span>
            {LIBRARY_TAB_LABELS[tab]}
            {on && <span className='ml-1 opacity-50'>{counts[tab]}</span>}
          </span>
        </button>
      )
    })}
  </div>
)

interface FiltersProps {
  options: { filter: LibraryFilter; count?: number }[]
  value: LibraryFilter | null
  onChange: (filter: LibraryFilter | null) => void
  className?: string
}

const FILTER_CLASSES =
  'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black/20 dark:focus-visible:ring-white/30'

/** "All  Furniture  Fashion  Fragrances": inline buttons, the picked one shaded. */
export const LibraryFilters = ({
  options,
  value,
  onChange,
  className,
}: FiltersProps) => {
  if (options.length === 0) return null
  const button = (
    label: string,
    filter: LibraryFilter | null,
    count?: number
  ) => {
    const on = filter === value
    return (
      <button
        key={filter ?? 'all'}
        type='button'
        aria-pressed={on}
        onClick={() => onChange(filter)}
        className={cn(
          FILTER_CLASSES,
          on
            ? 'bg-black/5 font-medium text-ink dark:bg-white/10 dark:text-white'
            : 'text-foreground-secondary hover:text-ink dark:hover:text-white'
        )}
      >
        {filter && isBottleList(filter) && (
          <ListIcon list={filter} on={on} className='size-3.5' />
        )}
        {label}
        {count != null && <span className='text-xs opacity-50'>{count}</span>}
      </button>
    )
  }
  return (
    <div
      role='group'
      aria-label='Filter'
      className={cn(
        'no-scrollbar flex items-center gap-0.5 overflow-x-auto py-1',
        className
      )}
    >
      {button('All', null)}
      {options.map(({ filter, count }) =>
        button(LIBRARY_FILTER_LABELS[filter], filter, count)
      )}
    </div>
  )
}

interface PagerProps {
  /** 0-based. */
  page: number
  pages: number
  /** More rows than read (the count reads "60+"). */
  more: boolean
  canPrev: boolean
  canNext: boolean
  loading?: boolean
  onPrev: () => void
  onNext: () => void
  className?: string
}

const PAGER_BUTTON =
  'inline-flex size-8 items-center justify-center rounded-full border border-black/10 outline-none transition-colors hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black/20 disabled:pointer-events-none disabled:opacity-40 dark:border-white/15 dark:hover:bg-white/5'

/** "‹ 1 / 6+ ›" under a list, following its scroll. */
export const LibraryPager = ({
  page,
  pages,
  more,
  canPrev,
  canNext,
  loading = false,
  onPrev,
  onNext,
  className,
}: PagerProps) => {
  const plus = more ? '+' : ''
  return (
    <nav
      aria-label='Pages'
      className={cn('flex shrink-0 items-center gap-3', className)}
    >
      <div className='flex items-center gap-2'>
        <button
          type='button'
          aria-label='Previous page'
          disabled={!canPrev}
          onClick={onPrev}
          className={PAGER_BUTTON}
        >
          <ChevronLeftIcon aria-hidden className='size-4' />
        </button>
        <span className='min-w-10 text-center text-xs tabular-nums'>
          {page + 1} / {pages}
          {plus}
        </span>
        <button
          type='button'
          aria-label='Next page'
          disabled={!canNext || loading}
          onClick={onNext}
          className={PAGER_BUTTON}
        >
          {loading ? (
            <Loader2 aria-hidden className='size-4 animate-spin' />
          ) : (
            <ChevronRightIcon aria-hidden className='size-4' />
          )}
        </button>
      </div>
    </nav>
  )
}

/**
 * Marks the search box, so a menu's Esc handler can leave Esc to it: the
 * first Esc closes the search, the next one the menu.
 */
export const LIBRARY_SEARCH_ATTR = 'data-library-search'

/** True for an Esc pressed in the Library's search box. */
export const isLibrarySearchEscape = (event: Event) =>
  event.target instanceof Element &&
  !!event.target.closest(`[${LIBRARY_SEARCH_ATTR}]`)

const ICON_BUTTON_CLASSES =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-full text-foreground-secondary outline-none transition-colors hover:bg-black/5 hover:text-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black/20 dark:hover:bg-white/5 dark:hover:text-white dark:focus-visible:ring-white/30'

/**
 * The search under the list: a magnifier that widens into a box when tapped,
 * at the page buttons' height so the row doesn't move. The box is focused when
 * it opens; an x clears it; Esc clears it, then folds it back; tapping away
 * from an empty box folds it too.
 */
export const LibrarySearch = ({
  id,
  open,
  value,
  onChange,
  onOpen,
  onClose,
  buttonRef,
  className,
}: {
  id: string
  open: boolean
  value: string
  onChange: (value: string) => void
  onOpen: () => void
  /** `refocus`: put focus back on the magnifier (not when focus left on its own). */
  onClose: (refocus: boolean) => void
  buttonRef?: React.Ref<HTMLButtonElement>
  className?: string
}) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (open) ref.current?.focus()
  }, [open])
  return (
    <div
      {...(open ? { [LIBRARY_SEARCH_ATTR]: '' } : {})}
      onKeyDown={(e) => {
        if (!open || e.key !== 'Escape' || e.nativeEvent.isComposing) return
        e.preventDefault()
        if (value) {
          onChange('')
          ref.current?.focus()
        } else onClose(true)
      }}
      onBlur={(e) => {
        if (
          open &&
          !value &&
          !e.currentTarget.contains(e.relatedTarget as Node | null)
        )
          onClose(false)
      }}
      className={cn(
        'flex h-8 min-w-0 basis-8 items-center rounded-full border transition-[flex-grow,border-color,background-color] duration-200 ease-out motion-reduce:transition-none',
        open
          ? 'grow border-black/10 bg-white focus-within:border-black/15 dark:border-white/10 dark:bg-black dark:focus-within:border-white/20'
          : 'grow-0 border-transparent',
        className
      )}
    >
      <button
        ref={buttonRef}
        type='button'
        aria-label={`Search your ${LIBRARY_LABEL}`}
        aria-expanded={open}
        // The box it points at is only drawn while open.
        aria-controls={open ? id : undefined}
        tabIndex={open ? -1 : undefined}
        // Once open it only puts the cursor in the box, so the box keeps focus.
        onMouseDown={open ? (e) => e.preventDefault() : undefined}
        onClick={open ? () => ref.current?.focus() : onOpen}
        className={cn(
          ICON_BUTTON_CLASSES,
          '-m-px',
          open &&
            'cursor-text hover:bg-transparent hover:text-foreground-secondary dark:hover:bg-transparent dark:hover:text-foreground-secondary'
        )}
      >
        <Search aria-hidden className='size-4' />
      </button>
      {open && (
        <input
          ref={ref}
          id={id}
          type='search'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Search your ${LIBRARY_LABEL.toLowerCase()}`}
          aria-label={`Search your ${LIBRARY_LABEL}`}
          autoComplete='off'
          spellCheck={false}
          className={cn(
            'min-w-0 flex-1 appearance-none !bg-transparent text-base text-ink outline-none duration-200 animate-in fade-in placeholder:text-foreground-secondary sm:text-sm dark:text-white [&::-webkit-search-cancel-button]:hidden',
            value ? 'pr-1' : 'pr-3'
          )}
        />
      )}
      {open && value && (
        <button
          type='button'
          aria-label='Clear search'
          onClick={() => {
            onChange('')
            ref.current?.focus()
          }}
          className={cn(ICON_BUTTON_CLASSES, '-m-px')}
        >
          <X aria-hidden className='size-4' />
        </button>
      )}
    </div>
  )
}
